import React from 'react';
import { Brain, Target, Zap, Award } from 'lucide-react';

const WhyUsSection = () => {
  const features = [
    {
      icon: Brain,
      title: "Retention-Science Editing",
      description: "We don't just cut and paste. Every frame is engineered using data-driven psychology to keep viewers watching until the last second. Our videos achieve 90%+ retention rates consistently."
    },
    {
      icon: Target,
      title: "Platform-Specific Optimization",
      description: "Each platform has unique algorithms. We optimize your content specifically for Instagram Reels, TikTok, YouTube Shorts, and LinkedIn to maximize reach and engagement."
    },
    {
      icon: Zap,
      title: "Lightning-Fast Turnaround",
      description: "In the social media game, speed wins. Get your polished, viral-ready videos back in 24-48 hours. Authority members enjoy 12-24 hour expedited delivery."
    },
    {
      icon: Award,
      title: "7-Figure Agency Quality",
      description: "Access the same editing expertise used by top influencers and brands, but at a fraction of the cost. No overhead, no management hassles, just premium results."
    }
  ];

  return (
    <section id="why-us" className="section-padding bg-brand-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-white">Why </span>
            <span className="text-brand-hot-pink">ViralVantage?</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We don't just edit videos — we engineer them to dominate algorithms and captivate audiences
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="card-hover bg-brand-dark border-2 border-brand-magenta/30 rounded-xl p-8 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="bg-brand-gradient rounded-lg p-4">
                      <Icon className="text-white" size={32} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Retention Science Highlight */}
        <div className="mt-16 bg-brand-gradient-subtle border-2 border-brand-hot-pink/30 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
            The Power of <span className="text-brand-coral">Retention-Science</span>
          </h3>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Most editors focus on making videos "look nice." We focus on making them <span className="font-bold text-brand-hot-pink">perform</span>. 
            Using proven psychological triggers, strategic pacing, pattern interrupts, and hook engineering, we keep viewers engaged 
            from the first frame to the final CTA. The result? More views, more engagement, and more business.
          </p>
          <div className="flex flex-wrap justify-center gap-8 mt-8 text-center">
            <div>
              <div className="text-4xl font-black text-brand-glow mb-2">3x</div>
              <div className="text-sm text-gray-400">Higher Completion Rate</div>
            </div>
            <div>
              <div className="text-4xl font-black text-brand-coral mb-2">5x</div>
              <div className="text-sm text-gray-400">More Shares</div>
            </div>
            <div>
              <div className="text-4xl font-black text-brand-hot-pink mb-2">10x</div>
              <div className="text-sm text-gray-400">Better ROI</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
